import clsx from "clsx";
import { ForwardedRef } from "react";
import { twMerge } from "tailwind-merge";
import { PropsWithAs } from "../types";
import { forwardRefWithGeneric } from "../utils";

export interface CardProps {
  hover?: boolean;
  outline?: boolean;
  variant?: "default" | "primary" | "secondary" | "light";
}

const Card = <T extends React.ElementType = "div">(
  { as, ...props }: PropsWithAs<CardProps, T>,
  ref: ForwardedRef<any>,
) => {
  const {
    className,
    onClick,
    hover = !!onClick,
    outline = false,
    variant = "default",
    ...rest
  } = props;
  const Component = as ?? "div";

  const variantClasses: Record<NonNullable<CardProps["variant"]>, string> = {
    primary: clsx(
      "bg-hunter-200 rounded-xl shadow-xl shadow-primary/25 *:text-primary-dark *:*:text-primary-dark",
      outline && "outline-2 outline-hunter-400/50",
      hover &&
        "hover:bg-hunter-100 hover:shadow-md hover:outline-hunter-300/50 cursor-pointer",
    ),
    secondary: clsx(
      "bg-primary-light rounded-xl shadow-xl shadow-primary/25",
      outline && "outline-2 outline-primary/50",
      hover && "hover:bg-hunter-50 hover:shadow-md cursor-pointer",
    ),
    light: clsx(
      "bg-linear-to-b from-white to-slate-50 rounded-xl shadow-xl",
      outline && "outline-2 outline-primary/50",
      hover && " hover:shadow-md cursor-pointer",
    ),
    default: clsx(
      "bg-white rounded-md",
      outline && "border border-slate-300",
      !outline && "shadow-xs",
      hover && "hover:bg-gunmetal-50 hover:shadow-md cursor-pointer",
    ),
  };

  return (
    <Component
      {...rest}
      ref={ref}
      onClick={onClick}
      className={twMerge(
        "text-secondary transition-all duration-300",
        variantClasses[variant],
        className,
      )}
    />
  );
};

export default forwardRefWithGeneric(Card);
